import { FormData, Workflow, FormFilters, PendingFormAssignment } from '../types/forms';
import { apiFetch } from '../utils/apiFetch';
import { API_BASE_URL } from '../utils/apiBaseUrl';
import { executeCapability } from '../utils/capabilityExecute';
import { formNamesMatch, sanitizeFormFileName } from '../utils/formFileName';

class FormService {
  private forms: FormData[] = [];
  private workflows: Workflow[] = [];
  private formsLoaded = false;

  async loadForms(force: boolean = false): Promise<void> {
    if (this.formsLoaded && !force) return;

    try {
      // Try to fetch list of forms from backend; fallback to static list
      let formFiles: string[] | null = null;
      const baseUrl = API_BASE_URL;
      const token = localStorage.getItem('lts_jwt_token');
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const res = await apiFetch(`${baseUrl}/forms`, { headers, cache: 'no-store' });
        if (res.ok) {
          const payload = await res.json();
          if (Array.isArray(payload.files)) formFiles = payload.files as string[];
        }
      } catch {}
      if (!formFiles) {
        // Only include forms that exist in public/forms/
        formFiles = [
          'jsa.json',
          'test-jsa.json'
        ];
      }

      const loadedForms: FormData[] = [];

      for (const fileName of formFiles) {
        try {
          const response = await apiFetch(`${baseUrl}/forms/definition/${encodeURIComponent(fileName)}`, {
            headers,
            cache: 'no-store',
          });
          if (response.ok) {
            const payload = await response.json();
            const raw = payload?.data ?? payload;
            const formData: FormData = this.migrateToV2(raw) as any;
            loadedForms.push(formData);
          }
        } catch (error) {
          console.error(`Failed to load form ${fileName}:`, error);
        }
      }

      this.forms = loadedForms;
      this.buildWorkflows();
      this.formsLoaded = true;
    } catch (error) {
      console.error('Failed to load forms:', error);
      throw error;
    }
  }

  async deleteForm(formName: string): Promise<void> {
    const baseUrl = API_BASE_URL
    const token = localStorage.getItem('lts_jwt_token')
    const headers: any = {}
    if (token) headers.Authorization = `Bearer ${token}`
    const encoded = encodeURIComponent(formName)
    const res = await apiFetch(`${baseUrl}/forms/${encoded}`, { method: 'DELETE', headers })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to delete form')
    }
    this.formsLoaded = false
    await this.loadForms(true)
  }

  async saveForm(
    formName: string,
    data: any,
    opts?: { previousFormName?: string },
  ): Promise<{ file: string }> {
    const baseUrl = API_BASE_URL
    const token = localStorage.getItem('lts_jwt_token')
    const headers: any = { 'Content-Type': 'application/json' }
    if (token) headers.Authorization = `Bearer ${token}`
    const previousFormName = String(opts?.previousFormName || '').trim()
    const res = await apiFetch(`${baseUrl}/forms`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        formName,
        data,
        ...(previousFormName ? { previousFormName } : {}),
      })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to save form')
    }
    return res.json()
  }

  async fetchFormByName(formName: string): Promise<any | null> {
    // Try to locate by scanning available files
    try {
      const baseUrl = API_BASE_URL;
      const token = localStorage.getItem('lts_jwt_token');
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
      const listRes = await apiFetch(`${baseUrl}/forms`, { headers, cache: 'no-store' });
      if (!listRes.ok) return null;
      const list = await listRes.json();
      if (!Array.isArray(list.files)) return null;
      const wantedFile = `${sanitizeFormFileName(formName)}.json`
      for (const file of list.files as string[]) {
        try {
          const res = await apiFetch(`${baseUrl}/forms/definition/${encodeURIComponent(file)}`, {
            headers,
            cache: 'no-store',
          });
          if (!res.ok) continue;
          const payload = await res.json();
          const raw = payload?.data ?? payload;
          const fileBase = String(file).replace(/\.json$/i, '')
          if (
            formNamesMatch(raw?.formName, formName)
            || fileBase === sanitizeFormFileName(formName)
            || String(file).toLowerCase() === wantedFile
          ) {
            return this.migrateToV2(raw);
          }
        } catch {}
      }
      return null;
    } catch {
      return null;
    }
  }

  private buildWorkflows(): void {
    const workflowMap = new Map<string, FormData[]>();

    // Group forms by workflow
    this.forms.forEach(form => {
      if (form.workflow) {
        if (!workflowMap.has(form.workflow)) {
          workflowMap.set(form.workflow, []);
        }
        workflowMap.get(form.workflow)!.push(form);
      }
    });

    // Convert to workflow objects
    this.workflows = Array.from(workflowMap.entries()).map(([name, forms]) => {
      // Sort by workflowOrder if provided, otherwise by formName
      const sortedForms = [...forms].sort((a, b) => {
        const aOrder = a.workflowOrder ?? Number.POSITIVE_INFINITY;
        const bOrder = b.workflowOrder ?? Number.POSITIVE_INFINITY;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.formName.localeCompare(b.formName);
      });
      return {
        name,
        forms: sortedForms,
        description: `${sortedForms.length} form${sortedForms.length > 1 ? 's' : ''} in this workflow`
      };
    });
  }

  getForms(filters?: FormFilters): FormData[] {
    let filteredForms = [...this.forms];

    if (filters?.category) {
      filteredForms = filteredForms.filter(form => 
        form.category.toLowerCase() === filters.category!.toLowerCase()
      );
    }

    if (filters?.workflow) {
      filteredForms = filteredForms.filter(form => 
        form.workflow === filters.workflow
      );
    }

    if (filters?.search) {
      const searchTerm = filters.search.toLowerCase();
      filteredForms = filteredForms.filter(form =>
        form.formName.toLowerCase().includes(searchTerm) ||
        form.description.toLowerCase().includes(searchTerm) ||
        form.category.toLowerCase().includes(searchTerm)
      );
    }

    return filteredForms;
  }

  // Migration from legacy schema to V2 schema (non-destructive)
  migrateToV2(raw: any): any {
    if (raw && raw.version === 2) return raw;
    const stepsOld = Array.isArray(raw?.steps) ? raw.steps : [];
    
    // Helper to migrate a single question (handles nested groups)
    const migrateQuestion = (q: any): any => {
      const migrated: any = {
        id: q.id,
        type: q.type,
        label: q.label,
        required: q.required !== false,
        readonly: !!q.readonly,
        fillerStage: Number(q.fillerStage) >= 1 ? Number(q.fillerStage) : 1,
        description: q.description,
        options: q.options,
        tool: q.tool,
        pdfField: q.pdfField || undefined,
        excludeFromCache: q.excludeFromCache,
      };
      // Handle group questions with nested questions
      if (q.type === 'group' && Array.isArray(q.questions)) {
        migrated.name = q.name;
        migrated.layout = q.layout;
        migrated.showHeading = q.showHeading;
        migrated.showDescription = q.showDescription;
        migrated.mode = q.mode;
        migrated.questions = q.questions.map(migrateQuestion);
      }
      return migrated;
    };
    
    const migratedSteps = stepsOld.map((s: any, idx: number) => ({
      id: s.id || `step_${idx+1}`,
      title: s.title || s.name || `Step ${idx+1}`,
      description: s.description,
      questions: Array.isArray(s.questions) ? s.questions.map(migrateQuestion) : []
    }));
    return {
      formName: raw.formName,
      workflow: raw.workflow ?? null,
      workflowOrder: raw.workflowOrder,
      workflowOptional: raw.workflowOptional ?? false,
      description: raw.description,
      category: raw.category,
      estimatedTime: raw.estimatedTime,
      tools: raw.tools || [],
      cache: raw.cache,
      steps: migratedSteps,
      fillers: raw.fillers,
      permissions: raw.permissions || [],
      accessLevels: Array.isArray(raw.accessLevels) ? raw.accessLevels : undefined,
      notifications: raw.notifications,
      pdf: raw.pdf || undefined,
      upload: raw.upload || undefined,
      status: raw.status || 'published',
      version: 2,
    };
  }

  getWorkflows(): Workflow[] {
    return [...this.workflows];
  }

  getFormById(formName: string): FormData | undefined {
    return this.forms.find(form => formNamesMatch(form.formName, formName));
  }

  getWorkflowByName(name: string): Workflow | undefined {
    return this.workflows.find(workflow => workflow.name === name);
  }

  getFormsInWorkflow(name: string): FormData[] {
    const workflow = this.getWorkflowByName(name);
    return workflow ? [...workflow.forms] : [];
  }

  getCategories(): string[] {
    const categories = new Set(this.forms.map(form => form.category));
    return Array.from(categories).sort();
  }

  getWorkflowNames(): string[] {
    return this.workflows.map(workflow => workflow.name).sort();
  }

  /**
   * Forms the signed-in user still needs to complete (in-progress instances).
   */
  async getPendingAssignments(): Promise<PendingFormAssignment[]> {
    try {
      const rows = await executeCapability<PendingFormAssignment[]>({
        app: 'forms',
        capability: 'forms.listPending',
      })
      return Array.isArray(rows) ? rows : []
    } catch {
      return []
    }
  }

  async getAllPendingAssignments(): Promise<PendingFormAssignment[]> {
    try {
      const rows = await executeCapability<PendingFormAssignment[]>({
        app: 'forms',
        capability: 'forms.listAllPending',
      })
      return Array.isArray(rows) ? rows : []
    } catch {
      return []
    }
  }

  async cancelInstance(instanceId: string) {
    return executeCapability({
      app: 'forms',
      capability: 'forms.cancelInstance',
      input: { instanceId },
    })
  }

  async downloadInstancePdf(instanceId: string): Promise<{ pdfBase64: string; fileName: string }> {
    return executeCapability({
      app: 'forms',
      capability: 'forms.downloadInstancePdf',
      input: { instanceId },
    })
  }

  async searchEmployees(q: string): Promise<Array<{ employeeId: string; name: string; preferredName?: string | null; position?: string | null }>> {
    return executeCapability({
      app: 'forms',
      capability: 'forms.searchEmployees',
      input: { q },
    })
  }

  async startInstance(formName: string) {
    return executeCapability({
      app: 'forms',
      capability: 'forms.startInstance',
      input: { formName },
    })
  }

  async getInstance(instanceId: string) {
    return executeCapability({
      app: 'forms',
      capability: 'forms.getInstance',
      input: { instanceId },
    })
  }

  async saveStage(instanceId: string, answers: Record<string, any>) {
    return executeCapability({
      app: 'forms',
      capability: 'forms.saveStage',
      input: { instanceId, answers },
    })
  }

  async submitStage(instanceId: string, answers: Record<string, any>) {
    return executeCapability({
      app: 'forms',
      capability: 'forms.submitStage',
      input: { instanceId, answers },
    })
  }

  isLoaded(): boolean {
    return this.formsLoaded;
  }
}

export const formService = new FormService(); 